import { Controller } from "@hotwired/stimulus";
import * as VoePay from "../../utils/voe_pay";

// Pay calculator + submit-time validation for the redesigned VoE employer form.
// Reveals the hours field (hourly), shows a live annualized readout, flags an invalid
// amount, and swaps to the deny step. Salary is entered as an annual amount; hourly is
// rate x hours x 52. On submit it surfaces the first unmet requirement as a visible inline
// message and prevents the POST; a fully valid submit proceeds natively (the server is the
// authoritative backstop).
//
// The server recomputes the annual figure authoritatively (Voe::PayAnnualizer is the source
// of truth; see spec/models/voe/pay_annualizer_spec.rb for the shared worked examples).

export default class extends Controller {
  static targets = [
    "structure",
    "structureError",
    "startDate",
    "startDateError",
    "amount",
    "amountError",
    "hours",
    "hoursError",
    "hoursRow",
    "formula",
    "result",
    "annualFigure",
    "attestation",
    "attestationError",
    "affirmSection",
    "denySection",
    "denyToggle",
    "declineReason",
    "declineReasonError",
  ];

  connect() {
    // A validation re-render on the deny path preserves the selected reason server-side; reopen
    // the deny step in that case instead of resetting to the affirmation view.
    if (this.declineReasonTargets.some((radio) => radio.checked)) {
      this.showDeny();
    } else {
      this.denySectionTarget.hidden = true;
      this.affirmSectionTarget.hidden = false;
    }
    this.refresh();
  }

  refresh() {
    if (this.attestationTarget.checked) this.attestationErrorTarget.hidden = true;
    if (this.startDateTarget.value) this.startDateErrorTarget.hidden = true;

    const structure = this.selectedStructure();
    if (structure !== null) this.structureErrorTarget.hidden = true;
    this.hoursRowTarget.hidden = structure !== "hourly";
    if (this.hoursValue() > 0) this.hoursErrorTarget.hidden = true;
    this.markAmountValidity();

    const amount = this.amountValue();
    const hours = this.hoursValue();
    const annual = VoePay.annualize(structure, amount, hours);
    if (annual === null) {
      // Direct the employer to the missing step instead of a dead readout: pick a structure
      // first (never defaulted — it drives the math), then the amount.
      this.formulaTarget.textContent = VoePay.promptText(structure);
      this.resultTarget.hidden = true;
      return;
    }
    this.formulaTarget.textContent = VoePay.formulaText(structure, amount, hours);
    this.annualFigureTarget.textContent = `${VoePay.currency(annual)} / yr`;
    this.resultTarget.hidden = false;
  }

  // Confirm and Submit response stay clickable; validation happens on submit so an unmet
  // requirement produces a visible message instead of a silently disabled button. Only the
  // failing branch calls preventDefault — a passing submit proceeds natively, so the clicked
  // button's employer_response value rides along and Turbo handles the response as usual.
  onSubmit(event) {
    if (this.submitPath(event) === "deny") this.validateDeny(event);
    else this.validateAffirm(event);
  }

  // Route by the clicked button's value; fall back to the visible section when the browser
  // doesn't populate event.submitter (older in-app email webviews), so a decline click never
  // runs affirmation checks.
  submitPath(event) {
    const value = event.submitter && event.submitter.value;
    if (value === "yes") return "affirm";
    if (value === "no") return "deny";
    return this.denySectionTarget.hidden ? "affirm" : "deny";
  }

  // Checks run top-to-bottom in the form's visual order (start date, then the compensation
  // block), so the first message points at the highest unmet field.
  validateAffirm(event) {
    if (!this.startDateTarget.value) {
      event.preventDefault();
      this.startDateErrorTarget.hidden = false;
      this.startDateTarget.focus();
      return;
    }
    const structure = this.selectedStructure();
    if (structure === null) {
      event.preventDefault();
      this.structureErrorTarget.hidden = false;
      this.structureTargets[0].focus();
      return;
    }
    if (!(this.amountValue() > 0)) {
      event.preventDefault();
      this.amountErrorTarget.hidden = false;
      this.amountTarget.classList.add("is-invalid");
      this.amountTarget.focus();
      return;
    }
    if (structure === "hourly" && !(this.hoursValue() > 0)) {
      event.preventDefault();
      this.hoursErrorTarget.hidden = false;
      this.hoursTarget.focus();
      return;
    }
    if (!this.attestationTarget.checked) {
      event.preventDefault();
      this.attestationErrorTarget.hidden = false;
      this.attestationTarget.focus();
    }
  }

  validateDeny(event) {
    if (this.declineReasonTargets.some((radio) => radio.checked)) return;

    event.preventDefault();
    this.declineReasonErrorTarget.hidden = false;
    this.declineReasonTargets[0].focus();
  }

  clearDeclineError() {
    this.declineReasonErrorTarget.hidden = true;
  }

  // Flag an amount only once something has been typed, so the field isn't red on an empty form.
  markAmountValidity() {
    const raw = String(this.amountTarget.value).trim();
    const invalid = raw !== "" && !(this.amountValue() > 0);
    this.amountErrorTarget.hidden = !invalid;
    this.amountTarget.classList.toggle("is-invalid", invalid);
  }

  // Deny is a separate step: swap the whole affirmation view for the reason step so the
  // employer never sees comp fields + Confirm alongside the deny reasons and Submit.
  showDeny() {
    this.affirmSectionTarget.hidden = true;
    this.denySectionTarget.hidden = false;
  }

  showAffirm() {
    this.denySectionTarget.hidden = true;
    this.affirmSectionTarget.hidden = false;
    this.refresh();
  }

  selectedStructure() {
    const checked = this.structureTargets.find((radio) => radio.checked);
    return checked ? checked.value : null;
  }

  amountValue() {
    return VoePay.parseAmount(this.amountTarget.value);
  }

  hoursValue() {
    return parseFloat(this.hoursTarget.value);
  }
}
